import type { PipelineSummaryItem } from "../../pages/pipeline/pipelineSummaryCondensed";
import {
  COLORS,
  FONTS,
  SLIDE_W,
  TABLE_ROW_BORDER,
  TITLE_H,
  addShape,
  addTableHeaderRule,
  addTitleBar,
  getShapes,
  type PptxInstance,
  type PptxSlide,
} from "./pptxCore";
import {
  buildSummaryDisplayRows,
  buildSummaryTableRows,
  chartColorsForCount,
  topThreeSectorInsight,
} from "./summaryTableHelpers";

/** Share of the chart frame given to the donut; the rest holds the legend. */
const PLOT_W_FRACTION = 0.55;

export type StatusSectorSlideInput = {
  statusItems: PipelineSummaryItem[];
  statusTotal: {
    deal_count: number;
    loan_amount_gbp: number;
    average_deal_size_gbp?: number;
  };
  sectorItems: PipelineSummaryItem[];
  sectorTotal: {
    deal_count: number;
    loan_amount_gbp: number;
    average_deal_size_gbp?: number;
  };
};

function addSectionDonutAndTable(
  pptx: PptxInstance,
  slide: PptxSlide,
  opts: {
    sectionTitle: string;
    y: number;
    height: number;
    items: PipelineSummaryItem[];
    total: {
      deal_count: number;
      loan_amount_gbp: number;
      average_deal_size_gbp?: number;
    };
  },
): void {
  const { RECTANGLE, ROUNDED_RECTANGLE } = getShapes(pptx);
  const padX = 0.28;
  const colW = [1.45, 0.55, 1.35, 1.3, 1.3];
  const tableW = colW.reduce((sum, width) => sum + width, 0);
  const chartW = 4.05;
  const gap = 0.25;
  const chartX = padX + tableW + gap;

  slide.addText(opts.sectionTitle, {
    x: padX,
    y: opts.y,
    w: 4,
    h: 0.28,
    fontFace: FONTS.body,
    fontSize: 14,
    bold: true,
    color: COLORS.textDark,
    margin: 0,
  });

  const contentY = opts.y + 0.32;
  const contentH = opts.height - 0.32;
  const rows = buildSummaryDisplayRows(opts.items, opts.total);
  const chartItems = opts.items.filter(
    (item) => item.loan_amount_gbp > 0 || item.deal_count > 0,
  );

  const rowH = contentH / (rows.length + 1);

  slide.addTable(buildSummaryTableRows(rows), {
    x: padX,
    y: contentY,
    w: tableW,
    h: contentH,
    colW,
    rowH,
    margin: [0.02, 0.06, 0.02, 0.06],
    border: TABLE_ROW_BORDER,
    fontFace: FONTS.body,
    fontSize: 8,
    color: COLORS.textDark,
    align: "left",
    valign: "middle",
  });

  addTableHeaderRule(pptx, slide, {
    x: padX,
    y: contentY,
    w: tableW,
    headerRowH: rowH,
  });

  const sliceColors = chartColorsForCount(chartItems.length);
  const colorByName = new Map(
    chartItems.map((item, index) => [item.name, sliceColors[index]]),
  );
  const barTrackX = padX + tableW - colW[4] + 0.07;
  const barTrackW = colW[4] - 0.49;
  const barH = 0.1;

  rows.forEach((row, index) => {
    if (row.isTotal) return;
    const barY = contentY + rowH * (index + 1) + (rowH - barH) / 2;
    addShape(slide, ROUNDED_RECTANGLE, {
      x: barTrackX,
      y: barY,
      w: barTrackW,
      h: barH,
      fill: { color: COLORS.barTrack },
      line: { color: COLORS.barTrack, width: 0 },
      rectRadius: barH / 2,
    });
    const fraction = Math.min(Math.max(row.percentOfTotal / 100, 0), 1);
    if (fraction <= 0) return;
    const fillColor = colorByName.get(row.name) ?? COLORS.muted;
    addShape(slide, ROUNDED_RECTANGLE, {
      x: barTrackX,
      y: barY,
      w: Math.max(barTrackW * fraction, barH),
      h: barH,
      fill: { color: fillColor },
      line: { color: fillColor, width: 0 },
      rectRadius: barH / 2,
    });
  });

  if (chartItems.length > 0) {
    slide.addChart(pptx.ChartType.doughnut, [
      {
        name: opts.sectionTitle,
        labels: chartItems.map((item) => item.name),
        values: chartItems.map((item) => item.loan_amount_gbp),
      },
    ], {
      x: chartX,
      y: contentY,
      w: chartW,
      h: contentH,
      showTitle: false,
      showPercent: false,
      showValue: false,
      showLabel: false,
      showLegend: true,
      legendPos: "r",
      legendFontFace: FONTS.body,
      legendFontSize: 9,
      // Pin the plot area left so both donuts share a size and edge regardless
      // of how wide their legend labels are.
      layout: { x: 0, y: 0, w: PLOT_W_FRACTION, h: 1 },
      chartColors: chartColorsForCount(chartItems.length),
      holeSize: 50,
    });
  } else {
    addShape(slide, RECTANGLE, {
      x: chartX,
      y: contentY,
      w: chartW,
      h: contentH,
      fill: { color: "F7F7F9" },
      line: { color: "E0E0E0", width: 1 },
    });
    slide.addText("No data", {
      x: chartX,
      y: contentY,
      w: chartW,
      h: contentH,
      fontFace: FONTS.body,
      fontSize: 12,
      color: COLORS.muted,
      align: "center",
      valign: "middle",
    });
  }
}

export function addStatusSectorSlide(
  pptx: PptxInstance,
  input: StatusSectorSlideInput,
  logoDataUri?: string | null,
): PptxSlide {
  const slide = pptx.addSlide();
  slide.background = { color: COLORS.white };

  addTitleBar(pptx, slide, {
    title: "Pipeline Analysis – Status & Sector",
    logoDataUri,
  });

  const bodyTop = TITLE_H + 0.18;
  const sectionH = 3.15;
  const sectionGap = 0.18;

  addSectionDonutAndTable(pptx, slide, {
    sectionTitle: "Status",
    y: bodyTop,
    height: sectionH,
    items: input.statusItems,
    total: input.statusTotal,
  });

  addSectionDonutAndTable(pptx, slide, {
    sectionTitle: "Sector",
    y: bodyTop + sectionH + sectionGap,
    height: sectionH,
    items: input.sectorItems,
    total: input.sectorTotal,
  });

  const insightX = 10.55;
  const insightY = bodyTop;
  const insightW = SLIDE_W - insightX - 0.28;
  const insightH = sectionH * 2 + sectionGap;

  addShape(slide, getShapes(pptx).ROUNDED_RECTANGLE, {
    x: insightX,
    y: insightY,
    w: insightW,
    h: insightH,
    fill: { color: COLORS.navy },
    line: { color: COLORS.navy, width: 0 },
    rectRadius: 0.12,
  });

  slide.addText(topThreeSectorInsight(input.sectorItems), {
    x: insightX + 0.16,
    y: insightY + 0.2,
    w: insightW - 0.32,
    h: insightH - 0.4,
    fontFace: FONTS.body,
    fontSize: 13,
    color: COLORS.white,
    align: "left",
    valign: "middle",
    margin: 0,
  });

  return slide;
}
